import { captureAndLogError } from '@/lib/error'

import loadGoogleMapsScript from './initializeGmap'

const predictionsCache: Record<string, google.maps.places.AutocompletePrediction[]> = {}
const PLACES_TYPES_TO_SEARCH = ['geocode', 'establishment']
const PLACES_TYPES_TO_FILTER = ['country']

export async function generateSessionToken() {
	try {
		await loadGoogleMapsScript()
		return new google.maps.places.AutocompleteSessionToken()
	} catch (e) {
		return (Math.random() * 100000).toFixed(0)
	}
}

const mapMatches = (matches?: google.maps.places.StringRange[]) =>
	matches?.map(({ startOffset, endOffset }) => ({
		offset: startOffset,
		length: endOffset - startOffset,
	})) ?? []

const normalizePrediction = (suggestion: google.maps.places.AutocompleteSuggestion) => {
	const p = suggestion.placePrediction
	if (!p) {
		return null
	}

	const fullText = p.text?.text ?? ''
	return {
		description: fullText,
		place_id: p.placeId ?? '',
		types: p.types ?? [],
		matched_substrings: mapMatches(p.text?.matches),
		structured_formatting: {
			main_text: p.mainText?.text ?? '',
			main_text_matched_substrings: mapMatches(p.mainText?.matches),
			secondary_text: p.secondaryText?.text ?? '',
			secondary_text_matched_substrings: mapMatches(p.secondaryText?.matches),
		},
		terms: fullText
			? fullText.split(', ').map((term) => ({
					value: term,
					offset: fullText.indexOf(term),
				}))
			: [],
		distance_meters: p.distanceMeters,
	} as unknown as google.maps.places.AutocompletePrediction
}

const filterCountries = (places: google.maps.places.AutocompletePrediction[]) =>
	places.filter((place) => place.types && !place.types.some((type) => PLACES_TYPES_TO_FILTER.includes(type)))

export async function searchAddress(
	input: string,
	sessionToken: google.maps.places.AutocompleteSessionToken,
	opts?: { countryCode?: string },
) {
	if (!input.length) {
		return []
	}

	const cacheKey = `${opts?.countryCode?.trim().toLowerCase() ?? '_'}:${input.trim().toLocaleLowerCase()}`
	await loadGoogleMapsScript()
	const request: google.maps.places.AutocompleteRequest = {
		input,
		sessionToken,
		includedPrimaryTypes: PLACES_TYPES_TO_SEARCH,
		includedRegionCodes: opts?.countryCode ? [opts.countryCode] : undefined,
	}

	if (predictionsCache.hasOwnProperty(cacheKey)) {
		return predictionsCache[cacheKey]
	}
	const { suggestions } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

	return new Promise<google.maps.places.AutocompletePrediction[]>((resolve) => {
		try {
			const predictions = suggestions
				.map(normalizePrediction)
				.filter((p): p is google.maps.places.AutocompletePrediction => !!p)

			const filteredResults = filterCountries(predictions)
			predictionsCache[cacheKey] = filteredResults
			resolve(filteredResults)
		} catch (e) {
			resolve([])
			captureAndLogError(e)
		}
	})
}

const makeLatLngLike = (latNum: number, lngNum: number) => {
	return {
		lat: () => latNum,
		lng: () => lngNum,
		toJSON: () => ({ lat: latNum, lng: lngNum }),
	} as unknown as google.maps.LatLng
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toLatLng = (loc: any): google.maps.LatLng | null => {
	if (!loc) {
		return null
	}
	if (typeof loc.lat === 'function' && typeof loc.lng === 'function') {
		return loc
	}
	if (typeof loc.lat === 'number' && typeof loc.lng === 'number') {
		return makeLatLngLike(loc.lat, loc.lng)
	}
	if (typeof loc.lat === 'object' && typeof loc.lng === 'object' && typeof loc.lat === 'function') {
		return loc
	}
	return null
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const toLatLngBounds = (vp: any): google.maps.LatLngBounds | null => {
	if (!vp) {
		return null
	}
	const ne = vp.northeast || vp.ne || vp.northEast
	const sw = vp.southwest || vp.sw || vp.southWest
	const neLatLng = toLatLng(ne)
	const swLatLng = toLatLng(sw)
	if (neLatLng && swLatLng) {
		try {
			return new google.maps.LatLngBounds(swLatLng, neLatLng)
		} catch (e) {
			return null
		}
	}
	return null
}

export async function getPlaceDetails(placeId: string) {
	const service = new google.maps.places.Place({
		id: placeId,
	})
	try {
		const { place: details } = await service.fetchFields({
			fields: [
				'addressComponents',
				'formattedAddress',
				'location',
				'viewport',
				'internationalPhoneNumber',
				'id',
				'types',
			],
		})

		if (!details) {
			return null
		}
		return {
			address_components: details.addressComponents?.map((c) => ({
				long_name: c.longText,
				short_name: c.shortText,
				types: c.types,
			})),
			formatted_address: details.formattedAddress,
			geometry: {
				location: toLatLng(details.location),
				viewport: toLatLngBounds(details.viewport),
			},
			international_phone_number: details.internationalPhoneNumber,
			place_id: details.id,
			types: details.types,
		} as google.maps.places.PlaceResult
	} catch (e) {
		captureAndLogError(e)
		return null
	}
}

export function getComponentByType(place: google.maps.places.PlaceResult, ...types: string[]) {
	return place.address_components?.find((ac) => ac.types.some((t) => types.includes(t)))
}

export function getCityName(place: google.maps.places.PlaceResult): string | undefined {
	return place.address_components?.find((adrComp) =>
		adrComp.types.some((type) => ['locality', 'administrative_area_level_3', 'postal_town'].includes(type)),
	)?.long_name
}

export function getCountryCode(place: google.maps.places.PlaceResult): string | undefined {
	return place.address_components?.find((adrComp) => adrComp.types.some((type) => type === 'country'))?.short_name
}

export function getStateName(place: google.maps.places.PlaceResult): string | undefined {
	return place.address_components?.find((adrComp) =>
		adrComp.types.some((type) => type === 'administrative_area_level_1'),
	)?.long_name
}

export function getAddressDetails(place: google.maps.places.PlaceResult) {
	return {
		building: getComponentByType(place, 'subpremise')?.long_name,
		city_name: getComponentByType(place, 'administrative_area_level_3', 'locality', 'postal_town')?.long_name,
		country_code: getComponentByType(place, 'country')?.short_name,
		district: getComponentByType(place, 'neighborhood')?.long_name,
		floor: getComponentByType(place, 'floor')?.long_name,
		postal_code: getComponentByType(place, 'postal_code')?.long_name,
		room: getComponentByType(place, 'room')?.long_name,
		state_code: getComponentByType(place, 'administrative_area_level_1')?.short_name,
		state_name: getComponentByType(place, 'administrative_area_level_1')?.long_name,
		street: getComponentByType(place, 'route')?.long_name,
		street_number: getComponentByType(place, 'street_number')?.long_name,
	}
}
